import React from 'react';

const TIERS = ['A', 'B', 'C', 'D'];
const TIER_STYLE = {
  A: 'bg-green-100 text-green-800',
  B: 'bg-lime-100 text-lime-800',
  C: 'bg-yellow-100 text-yellow-800',
  D: 'bg-red-100 text-red-800',
};

const pct = (v, d = 1) => (typeof v === 'number' && Number.isFinite(v) ? `${v.toFixed(d)}%` : 'n/a');

/**
 * Normalise the two shapes the backend returns for raw QC (backend/qc/survey_qc.py):
 * the full report (POST raw/qc) and the stored summary (datasets.metadata.qc).
 */
export function qcDigest(qc) {
  if (!qc || typeof qc !== 'object') return null;
  const counts = qc.counts || {};
  const total = qc.n_readings ?? TIERS.reduce((s, t) => s + (counts[t] || 0), 0);
  const pctD = typeof qc.pct_d === 'number' ? qc.pct_d : (total ? ((counts.D || 0) / total) * 100 : null);
  const recip = qc.reciprocal || null;
  const flagged = Array.isArray(qc.flagged) ? qc.flagged : [];
  const worst = Array.isArray(qc.worst) ? qc.worst : [];
  return {
    total,
    counts: TIERS.map((t) => [t, counts[t] || 0]),
    pctD,
    pctKept: qc.pct_kept ?? null,
    nFlagged: (counts.C || 0) + (counts.D || 0),
    worst: worst.length ? worst : flagged.slice(0, 10),
    reciprocalPairs: recip ? recip.n_pairs : (qc.n_reciprocal_pairs ?? null),
    reciprocal: recip,
    repeats: qc.repeats || null,
    lowSignal: qc.signal_floor ? (qc.signal_floor.n_low_v || 0) + (qc.signal_floor.n_low_i || 0) : (qc.n_low_signal ?? null),
    remote: qc.remote?.detected ?? qc.remote_detected ?? false,
    warnings: Array.isArray(qc.warnings) ? qc.warnings : [],
    isFull: !!qc.tiers,
    generatedAt: qc.generated_at || null,
  };
}

/**
 * Resistivity QC summary for a raw survey: A-D tier counts, % tier D, flagged readings,
 * reciprocal/repeat stats and warnings, with the full report expandable.
 * `qc` is the POST raw/qc report or the stored metadata.qc summary.
 */
function QcCard({ qc, running = false, onRun }) {
  const d = qcDigest(qc);
  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm" data-testid="qc-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Survey QC</h2>
        {onRun && (
          <button onClick={onRun} disabled={running}
            className="px-3 py-1 text-xs border border-border rounded-md hover:bg-muted disabled:opacity-50" data-testid="qc-run">
            {running ? 'Running...' : d ? 'Re-run QC' : 'Run QC'}
          </button>
        )}
      </div>
      {!d && <p className="text-sm text-muted-foreground" data-testid="qc-empty">QC has not been run on this survey yet.</p>}
      {d && (
        <>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="qc-tiers">
            {d.counts.map(([t, n]) => (
              <span key={t} className={`px-3 py-1 rounded-md text-sm font-semibold ${TIER_STYLE[t]}`} data-testid={`qc-tier-${t}`}>
                {t}: {n}
              </span>
            ))}
          </div>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div><dt className="font-medium text-muted-foreground mb-1">Readings</dt><dd className="font-semibold">{d.total}</dd></div>
            <div><dt className="font-medium text-muted-foreground mb-1">Tier D</dt><dd className="font-semibold" data-testid="qc-pct-d">{pct(d.pctD)}</dd></div>
            <div><dt className="font-medium text-muted-foreground mb-1">Kept (A-C)</dt><dd className="font-semibold">{pct(d.pctKept)}</dd></div>
            <div><dt className="font-medium text-muted-foreground mb-1">Flagged (C and D)</dt><dd className="font-semibold" data-testid="qc-flagged">{d.nFlagged}</dd></div>
            <div>
              <dt className="font-medium text-muted-foreground mb-1">Reciprocal pairs</dt>
              <dd className="font-semibold" data-testid="qc-reciprocal">
                {d.reciprocalPairs ?? 'n/a'}
                {d.reciprocal && d.reciprocal.median_pct != null && ` (median ${pct(d.reciprocal.median_pct, 2)}, max ${pct(d.reciprocal.max_pct, 2)})`}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-muted-foreground mb-1">Repeat readings</dt>
              <dd className="font-semibold" data-testid="qc-repeats">
                {d.repeats ? `${d.repeats.n_pairs ?? 0} pairs` : 'n/a'}
                {d.repeats && d.repeats.median_pct != null && ` (median ${pct(d.repeats.median_pct, 2)}, max ${pct(d.repeats.max_pct, 2)})`}
              </dd>
            </div>
            <div><dt className="font-medium text-muted-foreground mb-1">Low signal</dt><dd className="font-semibold">{d.lowSignal ?? 'n/a'}</dd></div>
            <div><dt className="font-medium text-muted-foreground mb-1">Remote electrode</dt><dd className="font-semibold">{d.remote ? 'Detected' : 'No'}</dd></div>
          </dl>
          {d.worst.length > 0 && (
            <div className="mt-4 text-sm" data-testid="qc-flags">
              <div className="font-medium text-muted-foreground mb-1">Worst readings</div>
              <ul className="text-xs space-y-1">
                {d.worst.map((f) => (
                  <li key={f.seq}>
                    <span className="font-semibold">#{f.seq}</span> tier {f.tier}
                    {f.eff_err_pct != null && `, ${pct(f.eff_err_pct, 1)} error`}
                    {Array.isArray(f.reasons) && f.reasons.length > 0 && `: ${f.reasons.join('; ')}`}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {d.warnings.length > 0 && (
            <ul className="mt-4 text-xs text-yellow-800 bg-yellow-100 rounded p-3 list-disc list-inside" data-testid="qc-warnings">
              {d.warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          )}
          <details className="mt-4" data-testid="qc-report">
            <summary className="text-xs cursor-pointer text-muted-foreground">QC report{d.isFull ? '' : ' (summary; run QC for the full report)'}</summary>
            <pre className="mt-2 text-xs bg-muted rounded p-3 overflow-auto max-h-80">{JSON.stringify(qc, null, 2)}</pre>
          </details>
          {d.generatedAt && <p className="mt-2 text-xs text-muted-foreground">Generated {d.generatedAt}</p>}
        </>
      )}
    </div>
  );
}

export default QcCard;
